import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as currency from '@shopify/theme-currency';
import {getSizedImageUrl} from '@shopify/theme-images';
import {BlockIcon, ClockIcon, InfoIcon} from './icons';
import { pushToCartEvent } from '../ga4-events-scripts';

class CartItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rootUrl: window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '',
      minQuantity: 0,
      maxQuantity: 0,
      inclusions: [],
    };
  }

  handleChange(key, qty, max = 0, isAutoUpsell = false) {
    if (max > 0 && qty > max) {
      window.console.log(`Max ${max} items in cart.`);
      return;
    }

    const updateCart = this.props.updateCart;
    const data = {
      updates: {
        [key]: qty,
      },
    };

    const updateDataLayer = (data) => {
      function findMissingItem(responseCartID, initCartID) {
        // Convert responseCartID to a Set for efficient lookup
        let set1 = new Set(responseCartID);
      
        // Iterate over initCartID to find the missing item
        for (let i = 0; i < initCartID.length; i++) {
          let item = initCartID[i];
          if (!set1.has(item)) {
            return item;
          }
        }
      
        // Return null if no missing item is found
        return null;
      }
      
      const returnedItemID = data.items.map(item => item.variant_id) // [123123123213]
      const cartItemID = window.theme.initCart.items.map(item => item.variant_id) // [12398128340928, 123123123213]
      const itemDeleted = findMissingItem(returnedItemID, cartItemID) // returns 12398128340928 || null
      
      if (itemDeleted) {
        const deletedItem = window.theme.initCart.items.find(item => item.variant_id === itemDeleted)
        pushToCartEvent('ga_remove_from_cart', itemDeleted, deletedItem)
        window.theme.initCart = data
      }
      if (!itemDeleted) {
        const selectedVariant = data.items[0]
        const matchingItemInCart = window.theme.initCart.items.find(item => item.id === selectedVariant.id)
        const newQuantity = selectedVariant.quantity
        const existingQuantity = matchingItemInCart.quantity
        if (newQuantity > existingQuantity) { 
          pushToCartEvent('ga_add_to_cart', selectedVariant.id, matchingItemInCart)
          window.theme.initCart = data
        }
        if (newQuantity < existingQuantity) { 
          pushToCartEvent('ga_remove_from_cart', selectedVariant.id, matchingItemInCart)
          window.theme.initCart = data
        }
      }
    }

    /**
     * Check if this item is an auto upsell and if so flag that the user
     * has manually removed the product. This upsell, along with an other
     * auto upsells, will then fallback to being regular upsells that can
     * be added to the cart at any time.
     */
    if (isAutoUpsell) {
      window.localStorage.setItem('removedAutoUpsell', Date.now() + 1000 * 60 * 60 * 24);
    }

    axios({
      method: 'POST',
      url: `${this.state.rootUrl}/cart/update.js`,
      data,
      responseType: 'JSON',
    })
      .then(response => {
        updateDataLayer(response.data)
        updateCart(response.data);
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: response.data?.items
        }))
      })
      .catch(err => window.console.log(err));
  }

  async componentDidUpdate(prevProps) {
    if (this.props.cartItemsTagDetails !== prevProps.cartItemsTagDetails) {
      const cartItemTagDetails = this.props.cartItemsTagDetails.find(
        minMaxItem => minMaxItem.id === this.props.item.product_id
      );
      if (cartItemTagDetails) {
        this.setState({
          minQuantity: cartItemTagDetails.min,
          maxQuantity: cartItemTagDetails.max,
          isMachine: cartItemTagDetails.isMachine,
          inclusions: cartItemTagDetails.inclusions,
        });
      }
    }
  }

  render() {
    const {item} = this.props;
    const itemPrice = item.final_line_price;
    const itemOriginalPrice = item.original_line_price;
    const itemSaving = itemOriginalPrice - itemPrice;
    const isMachine = this.state.isMachine ? this.state.isMachine : false;
    const isAutoUpsell = item.properties && item.properties._upsell && item.properties._autoupsell;
    const isPreorder = Boolean(item.properties && item.properties.Preorder);
    const preorderDate = isPreorder && item.properties.Preorder !== 'true' && item.properties.Preorder;
    const preorderLabel = preorderDate
      ? window.theme.strings.preorderFor.replace('{{ date }}', preorderDate)
      : window.theme.strings.preorder;
    const estimatedAvailabilityDate = item.properties?.[window.theme?.strings?.estimatedAvailabilityDate];
    const isSalesForce = item.tags.includes('from-salesforce')
    const showRemove = !isAutoUpsell || (item.discounts.length > 0 && itemPrice > 0);
    const sizedImage = item.image ? getSizedImageUrl(item.image, '160x160_crop_center') : 'defaultImage.png';
    const newUpSell = item.properties?._new_upsell;
    const isMaxQuantityReached = this.state.maxQuantity > 0 && item.quantity >= this.state.maxQuantity;
    const buttonIsHidden =  isMaxQuantityReached || newUpSell;
    const buttonClassName = `cart-item__quantity-button cart-item__quantity-button--plus ${buttonIsHidden ? 'hidden' : ''}`;

    return (
      <li className="cart-item">
        <div className="cart-item__body">
          <div className="cart-item__image">
            <a href={item.url}>
              <img loading="lazy" src={sizedImage} alt={item.title} />
            </a>
          </div>
          <div className="cart-item__content">
            <p className="cart-item__title">{item.product_title}</p>
            <p className="cart-item__description">{item.variant_title}</p>
            {isPreorder && <p className="cart-item__preorder">{preorderLabel}</p>}
            <div className="cart-item__details">
              {showRemove && (
                <div className="cart-item__quantity">
                  <label className="visually-hidden">{window.theme.strings.quantity}</label>
                  {!isSalesForce && (
                      <button
                          className="cart-item__quantity-button cart-item__quantity-button--minus"
                          onClick={() => this.handleChange(item.key, item.quantity - 1, 0, isAutoUpsell)}
                      >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            className="icon icon-minus"
                            viewBox="0 0 20 20"
                        >
                          <path d="M17.543 11.029H2.1A1.032 1.032 0 0 1 1.071 10c0-.566.463-1.029 1.029-1.029h15.443c.566 0 1.029.463 1.029 1.029 0 .566-.463 1.029-1.029 1.029z" />
                        </svg>
                      </button>
                    )}
                  <span className="cart-item__qty">{item.quantity}</span>
                  {!isSalesForce &&(
                      <button
                          className={buttonClassName}
                          onClick={() => this.handleChange(item.key, item.quantity + 1, this.state.maxQuantity)}
                      >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            className="icon icon-plus"
                            viewBox="0 0 20 20"
                        >
                          <path d="M17.409 8.929h-6.695V2.258c0-.566-.506-1.029-1.071-1.029s-1.071.463-1.071 1.029v6.671H1.967C1.401 8.929.938 9.435.938 10s.463 1.071 1.029 1.071h6.605V17.7c0 .566.506 1.029 1.071 1.029s1.071-.463 1.071-1.029v-6.629h6.695c.566 0 1.029-.506 1.029-1.071s-.463-1.071-1.029-1.071z" />
                        </svg>
                      </button>
                    )}
                </div>
              )}
              {itemSaving > 0 ? (
                <div className="cart-item__price">
                  <span className="cart-item__discounted-price">
                    {currency.formatMoney(itemPrice, window.theme.moneyFormat)}
                  </span>
                  <s className="cart-item__original-price">
                    {currency.formatMoney(itemOriginalPrice, window.theme.moneyFormat)}
                  </s>
                  <span className="cart-item__saving">
                    {window.theme.strings.saveOff.replace(
                      '{{ amount }}',
                      currency.formatMoney(itemSaving, window.theme.moneyFormat)
                    )}
                  </span>
                  {item.discounts.length > 0 && (
                    <div className="cart-item__discounts">
                      {item.discounts.map((discount, index) => (
                        <span key={index}>{discount.title}</span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="cart-item__price">{currency.formatMoney(itemPrice, window.theme.moneyFormat)}</div>
              )}
            </div>
            {item.quantity < this.state.minQuantity && (
              <div className="cart-item__message">
                {window.theme.strings.minMax.minimumMessage.replace('{{ quantity }}', this.state.minQuantity)}
              </div>
            )}
            {this.state.maxQuantity > 0 && item.quantity >= this.state.maxQuantity && (
              <div className="cart-item__message">
                {window.theme.strings.minMax.maximumMessage.replace('{{ quantity }}', this.state.maxQuantity)}
              </div>
            )}
            {showRemove && (
              <button className="cart-item__remove" onClick={() => this.handleChange(item.key, 0, 0, isAutoUpsell)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                  <g fill="none" fillRule="evenodd" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                    <path d="M4 4l12 12M16 4L4 16" />
                  </g>
                </svg>
              </button>
            )}
          </div>
        </div>
        {(estimatedAvailabilityDate || this.state.inclusions.length > 0) && (
          <div className="cart-item__inclusions">
            {estimatedAvailabilityDate && (
              <div className="estimated-availability">
                <ClockIcon />
                <span className="estimated-availability__label">
                  {window.theme.strings.estimatedAvailabilityDate} {estimatedAvailabilityDate}
                </span>
                <span className="estimated-availability__details">
                  <InfoIcon />
                  <span
                    className="estimated-availability__tooltip"
                    dangerouslySetInnerHTML={{__html: window.theme.strings.estimatedAvailabilityDetails}}
                  />
                </span>
              </div>
            )}
            {this.state.inclusions.length > 0 && (
              <div className="key-features">
                <h4 className="key-features__title">{window.theme.strings.inclusions}</h4>
                <ul className="key-features__list">
                  {this.state.inclusions.map(inclusion => (
                    <li key={inclusion.id} className="key-features__item">
                      <div className="key-features__icon">
                        <BlockIcon icon={inclusion.icon} />
                      </div>
                      <span dangerouslySetInnerHTML={{__html: inclusion.label}} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </li>
    );
  }
}

CartItem.propTypes = {
  item: PropTypes.object,
  updateCart: PropTypes.func,
  removeUpsell: PropTypes.func,
  cartItemsTagDetails: PropTypes.array,
};

export default CartItem;
